import React, { useEffect, useState, useRef, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import {
  ClickOutside,
} from 'components';
import { HamburgerIcon } from 'components/Icons';
import { useIsSmallScreen, useWindowWidth } from 'utils/hooks';
import * as uiActions from 'actions/ui';
import {
  MENU_ICON_SIZE,
  SEARCH_FIELD_MAX_WIDTH,
  useBodyScrollLock,
  getCurrentScrollPosition,
} from './navigationUtils';
import TopNavigation from './TopNavigation';
import PersistentNavigation from './PersistentNavigation';

import { useGetHeaderDataLazyQuery } from './headerQuery.generated';
import {
  HeaderContainer,
  HeaderSpacer,
  Content,
  LogoLink,
  Hamburger,
  LogoImage,
  MenuNavigationWrapper,
  PersistentNavigationContainer,
  NavigationOverlay,
  MenuWrapper,
} from './Styles';

const DEFAULT_MENU_ITEM_AVAILABILITY = ['logged-in', 'logged-out', 'open-service'];

function isMenuItemVisible(availability, isLoggedIn, isOpenServiceActive) {
  const availabilityRules = availability || DEFAULT_MENU_ITEM_AVAILABILITY;
  return (availabilityRules.includes('logged-in') && isLoggedIn)
    || (availabilityRules.includes('open-service') && isOpenServiceActive)
    || (availabilityRules.includes('logged-out') && !isLoggedIn);
}

function Header() {
  const [scrollStarted, setScrollStarted] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const navRef = useRef(null);
  const logoRef = useRef(null);
  const contentRef = useRef(null);
  const verticalNavRef = useRef(null);
  const persistentNavRef = useRef(null);
  const windowWidth = useWindowWidth();
  const isSmallScreen = useIsSmallScreen();
  const dispatch = useDispatch();

  const {
    brandName,
    headerLogo,
    headerTransparencySupported,
    headerOpacity,
    isMobileOS,
    paymentMandatory,
    entitledPacks,
    userLoggedIn,
    horizontalMenuHidden,
    isAnonymousUser,
    signUpEnabled,
    navigation,
    isOpenServiceActive,
    isCreateAccountEnabled,
    partner,
  } = useSelector(state => ({
    brandName: state.settings.brand.name,
    headerLogo: state.settings.brand.headerLogo,
    headerTransparencySupported: state.ui.headerTransparency[
      `${state.router.location.name}:${state.router.location.name === 'home'
        ? state.settings.navigation.menu[state.settings.navigation.defaultIndex].identifier
        : state.router.location.params.id}`
    ],
    headerOpacity: state.settings.headerOpacity,
    isMobileOS: state.common.isMobileOS,
    entitledPacks: state.packs.entitled,
    userLoggedIn: state.auth.isLoggedIn,
    paymentMandatory: state.auth.isLoggedIn
      && state.settings.features.payment?.onboarding?.mandatory,
    horizontalMenuHidden: state.ui.horizontalMenuHidden,
    isAnonymousUser: state.auth.isAnonymousUser,
    signUpEnabled: state.settings.features.register,
    navigation: state.settings.navigation,
    isOpenServiceActive: state.country.isOpenServiceActive,
    isCreateAccountEnabled: state.country.isCreateAccountEnabled,
    partner: state.settings.partner,
  }), shallowEqual);

  const entitlementRequest = useRef(false);
  const [ loadEntitlements, { data } ] = useGetHeaderDataLazyQuery();

  const isVerticalMenuOpen = menuExpanded && horizontalMenuHidden;

  useBodyScrollLock(isVerticalMenuOpen);

  if (!entitlementRequest.current && paymentMandatory && userLoggedIn && !entitledPacks) {
    entitlementRequest.current = true;
    loadEntitlements();
  }

  useEffect(() => {
    let unmounted = false;
    const onScroll = () => {
      if (unmounted) {
        return;
      }

      setScrollStarted(getCurrentScrollPosition() > 10);
    };

    const onScrollDebounced = debounce(onScroll, 10);

    window.addEventListener('scroll', onScrollDebounced);

    return () => {
      unmounted = true;
      window.removeEventListener('scroll', onScrollDebounced);
    };
  }, []);

  useEffect(() => {
    if (windowWidth) {
      const styles = window.getComputedStyle(contentRef.current);
      const padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const logoWidth = logoRef.current?.clientWidth ?? 0;
      const persistentNavWidth = persistentNavRef.current?.clientWidth ?? 0;
      const horizontalNavWidth = navRef.current?.clientWidth ?? 0;
      const containerWidth = contentRef.current?.clientWidth ?? 0;
      const freeSpace = containerWidth - horizontalNavWidth - persistentNavWidth - logoWidth - padding;
      const hideMenu = freeSpace < SEARCH_FIELD_MAX_WIDTH;

      dispatch(uiActions.hideHorizontalMenu(hideMenu || isSmallScreen || isMobileOS));
    }
  }, [windowWidth, isSmallScreen, isMobileOS, userLoggedIn, isAnonymousUser]);

  const closeMenu = () => setMenuExpanded(false);
  const menuToggle = () => setMenuExpanded(prevState => !prevState);

  const handleOverlayClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeMenu();
  };

  const handleOverlayTouch = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // see https://magine.atlassian.net/browse/MDM-10551
  const headerContainerOpacity = useMemo(() => {
    let beforeScroll = {
      gradientStart: headerOpacity.regular,
      gradientEnd: headerOpacity.regular,
    };
    let afterScroll = headerOpacity.regular;
    if (headerTransparencySupported) {
      beforeScroll = {
        gradientStart: headerOpacity.transparentStart,
        gradientEnd: headerOpacity.transparent,
      };
    }

    return { beforeScroll, afterScroll };
  }, [headerTransparencySupported, menuExpanded, horizontalMenuHidden]);

  const hideItemsForUnentitled = useMemo(
    () => paymentMandatory &&
      !data?.viewer.entitlements.edges.length
      && !entitledPacks?.length,
    [data?.viewer.entitlements],
  );

  // Sappa has signup available but hides it for end users
  const shouldShowSignUp = signUpEnabled && isCreateAccountEnabled && (!userLoggedIn || isAnonymousUser) && partner !== 'sappa';

  const { visibleMenuItems, persistentNavItems } = useMemo(() => {
    if (!navigation?.menu) return { visibleMenuItems: [], persistentNavItems: [] };

    const allVisibleItems = navigation.menu.filter(item =>
      isMenuItemVisible(item.available, userLoggedIn && !isAnonymousUser, isOpenServiceActive));

    const persistentTypes = ['search', 'settings'];
    const persistentItems = allVisibleItems.filter(item => persistentTypes.includes(item.type));
    const regularItems = allVisibleItems.filter(item => !persistentTypes.includes(item.type));

    return {
      visibleMenuItems: regularItems,
      persistentNavItems: persistentItems,
    };
  }, [navigation?.menu, userLoggedIn, isAnonymousUser, isOpenServiceActive]);

  const defaultMenuItem = navigation?.menu?.[navigation?.defaultIndex];

  return (
    <>
      <HeaderContainer
        opacity={headerContainerOpacity}
        scroll={`${scrollStarted}`}
      >
        <ClickOutside wrapperRef={verticalNavRef} onClickOutside={menuExpanded ? closeMenu : null}>
          <Content innerRef={contentRef}>
            <div ref={logoRef}>
              <LogoLink to={{ name: 'home' }}>
                <LogoImage
                  src={headerLogo}
                  alt={brandName}
                />
              </LogoLink>
            </div>
            <MenuWrapper isHidden={horizontalMenuHidden === undefined}>
              <MenuNavigationWrapper offCanvas={horizontalMenuHidden} aria-hidden={horizontalMenuHidden}>
                <TopNavigation
                  position="horizontal"
                  innerRef={navRef}
                  hideItemsForUnentitled={hideItemsForUnentitled}
                  visibleMenuItems={visibleMenuItems}
                  defaultMenuItem={defaultMenuItem}
                />
              </MenuNavigationWrapper>
              <NavigationOverlay
                isVisible={isVerticalMenuOpen}
                onClick={handleOverlayClick}
                onTouchStart={handleOverlayTouch}
                onTouchEnd={handleOverlayClick}
              />
              <TopNavigation
                position="vertical"
                isExpanded={isVerticalMenuOpen}
                hideItemsForUnentitled={hideItemsForUnentitled}
                onCloseMenu={closeMenu}
                innerRef={verticalNavRef}
                shouldShowSignUp={horizontalMenuHidden && shouldShowSignUp}
                visibleMenuItems={visibleMenuItems}
                defaultMenuItem={defaultMenuItem}
              />
              {/* Vertical persistent navigation - only render when menu is vertical */}
              {horizontalMenuHidden && (
                <PersistentNavigationContainer data-cy="persistent-navigation-mobile">
                  <PersistentNavigation
                    shouldShowSignUp={false}
                    persistentNavItems={persistentNavItems}
                  />
                  <Hamburger
                    onClick={menuToggle}
                    visible={horizontalMenuHidden && (visibleMenuItems.length > 0 || shouldShowSignUp)}
                    data-cy="hamburger-menu"
                  >
                    <HamburgerIcon size={MENU_ICON_SIZE} />
                  </Hamburger>
                </PersistentNavigationContainer>
              )}
              {/* Horizontal persistent navigation - always render, off-canvas when not in use for width calculation */}
              <PersistentNavigationContainer
                offCanvas={horizontalMenuHidden}
                innerRef={persistentNavRef}
                aria-hidden={horizontalMenuHidden}
                data-cy="persistent-navigation"
              >
                <PersistentNavigation
                  shouldShowSignUp={shouldShowSignUp}
                  persistentNavItems={persistentNavItems}
                />
              </PersistentNavigationContainer>
            </MenuWrapper>
          </Content>
        </ClickOutside>
      </HeaderContainer>

      {!headerTransparencySupported && (
        <HeaderSpacer />
      )}
    </>
  );
}

export default React.memo(Header);
